import { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getActiveCountriesRequest } from "../../redux/Dashboard/dashboardSlice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useAppTheme } from "../../theme/useAppTheme";
import { pageBackground, primaryButtonSx } from "../../theme/resolveThemeColors";

export default function LoginPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { activeCountries = [] } = useSelector(
    (state) => state.dashboard || {}
  );
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedCountryId, setSelectedCountryId] = useState("");
  const [selectedDialCode, setSelectedDialCode] = useState("+91");
  const [selectedPhoneLength, setSelectedPhoneLength] = useState(10);
  const themeColors = useAppTheme();
  useEffect(() => {
    dispatch(
      getActiveCountriesRequest({
        jeweller: `${process.env.REACT_APP_JEWELLER_ID}`,
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (activeCountries.length > 0 && !selectedCountryId) {
      const defaultCountry = activeCountries.find(
        (c) => c.dialCode === selectedDialCode
      );

      if (defaultCountry) {
        setSelectedCountryId(defaultCountry._id);
        setSelectedPhoneLength(defaultCountry.phoneLength || 10);
      }
    }
  }, [activeCountries, selectedDialCode, selectedCountryId]);

  const handleGenerateOtp = async () => {
    if (!phone) {
      toast.warning("Please enter your phone number.");
      return;
    }

    if (phone.length !== selectedPhoneLength) {
      toast.warning(`Phone number must be ${selectedPhoneLength} digits.`);
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/otp-generator/generateotp`,
        {
          otpSrc: phone,
          otpFor: "Customer",
          otpType: "LOGIN",
          jeweller: process.env.REACT_APP_JEWELLER_ID,
          countryID: selectedCountryId,
        },
        {
          headers: {
            version: "1.1.1",
          },
        }
      );

      console.log("Response:", response.data);

      if (response.data?.success) {
        localStorage.setItem("otpPhone", phone);
        localStorage.setItem("countryID", selectedCountryId);
        navigate("/GenerateOtp", {
          state: {
            phone,
            countryID: selectedCountryId,
            otpData: response.data.data,
          },
        });
      } else {
        toast.error(response.data?.message || "Something went wrong");
      }
    } catch (error) {
      console.error("Error generating OTP:", error);
      toast.error(error.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: pageBackground(themeColors),
        backgroundSize: "cover",
        backgroundPosition: "center",
        color: themeColors.scaffoldText,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 2, sm: 4 },
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 542,
          borderRadius: 3,
          boxShadow: 6,
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography
            variant="h4"
            textAlign="left"
            gutterBottom
            sx={{
              fontSize: { xs: "1.8rem", sm: "48px" },
              fontWeight: 500,
              color: themeColors.scaffoldText,
            }}
          >
            Welcome Back!
            <Box
              component="span"
              sx={{
                display: "block",
                color: themeColors.primary,
                fontSize: { xs: "1.8rem", sm: "42px" },
                fontWeight: 500,
              }}
            >
              Login with OTP
            </Box>
          </Typography>

          {/* Phone Number Field with Dial Code Dropdown */}
          <TextField
            label="Phone Number"
            variant="outlined"
            placeholder="Enter Phone Number"
            type="tel"
            value={phone}
            onChange={(e) => {
              const digitsOnly = e.target.value.replace(/\D/g, "");
              if (digitsOnly.length <= selectedPhoneLength)
                setPhone(digitsOnly);
            }}
            fullWidth
            sx={{ mb: 3 }}
            inputProps={{ maxLength: selectedPhoneLength }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Select
                    value={selectedDialCode}
                    onChange={(e) => {
                      const country = activeCountries.find(
                        (c) => c.dialCode === e.target.value
                      );
                      setSelectedDialCode(e.target.value);
                      setSelectedCountryId(country?._id);
                      setSelectedPhoneLength(country?.phoneLength || 10);
                    }}
                    sx={{
                      height: "40px",
                      minWidth: "80px",
                      "& .MuiSelect-select": {
                        padding: "8px 6px",
                      },
                    }}
                  >
                    {activeCountries.map((c) => (
                      <MenuItem key={c._id} value={c.dialCode}>
                        {c.dialCode}
                      </MenuItem>
                    ))}
                  </Select>
                </InputAdornment>
              ),
            }}
          />

          {/* Generate OTP Button */}
          <Button
            variant="contained"
            fullWidth
            sx={primaryButtonSx(themeColors, {
              fontSize: { xs: "1rem", sm: "24px" },
              py: 1.5,
            })}
            onClick={handleGenerateOtp}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Generate OTP"
            )}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}
